/**
 * VÉRAULT Portal — Client · Flight Journey Card
 *
 * Renders a published flight journey inside the proposal's Flights section.
 * Data is the client-safe snapshot written at publish time — internal
 * advisor notes never reach this surface.
 */
import React from "react";
import { Plane, ArrowRight, Clock, Users, Hourglass, Check, MessageCircle, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Surface, api } from "../../../index";
import { formatDate } from "../../../lib/format";
import { AdvisorRecommendationBadge } from "../../../components/AdvisorRecommendationBadge";

const REVISION_PROMPTS = [
  "Lower price",
  "Different airline",
  "Fewer stops",
  "Longer layover",
  "Shorter travel time",
  "Different departure time",
  "Other",
];

/** Per-journey Approve / Request Revision (modular approvals · Phase 5 preview). */
function ApprovalActions({ journey }) {
  const isAdvisorPreview =
    typeof window !== "undefined" && (window.location.pathname || "").includes("/portal/advisor/");
  const [status, setStatus] = React.useState(journey.client_status || null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [reasons, setReasons] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const tid = `flight-journey-${journey.id}`;

  const approve = async () => {
    if (isAdvisorPreview) return;
    if (!window.confirm(`Approve the ${journey.label} flights? Your advisor will begin securing these arrangements.`)) return;
    setBusy(true);
    try {
      await api.post(`/client-portal/flight-journeys/${journey.id}/approve`);
      setStatus("approved");
      setPanelOpen(false);
      window.dispatchEvent(new CustomEvent("vt:confirmations:refresh"));
      toast.success("Flights approved — your advisor has been notified.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record your approval just now.");
    } finally {
      setBusy(false);
    }
  };

  const sendRevision = async () => {
    if (isAdvisorPreview) return;
    if (!reasons.length && !message.trim()) {
      toast.error("Please choose a prompt or tell us what you'd like changed.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client-portal/flight-journeys/${journey.id}/request-revision`, {
        reasons,
        message: message.trim(),
      });
      setStatus("revision_requested");
      setPanelOpen(false);
      setReasons([]);
      setMessage("");
      window.dispatchEvent(new CustomEvent("vt:confirmations:refresh"));
      toast.success("Request sent — it's attached to this journey for your advisor.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send your request just now.");
    } finally {
      setBusy(false);
    }
  };

  const toggleReason = (r) =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  return (
    <div className="pt-3 border-t border-[rgba(0,0,0,0.05)] space-y-3" data-testid={`${tid}-approval`}>
      {status === "approved" ? (
        <div className="flex items-center gap-2" data-testid={`${tid}-approved-state`}>
          <span className="inline-flex items-center justify-center size-5 rounded-full bg-[hsl(var(--accent-gold))]/15">
            <Check className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={2} />
          </span>
          <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--accent-gold))]">
            Flights approved
          </span>
          <span className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            Your advisor will begin securing these arrangements.
          </span>
        </div>
      ) : (
        <>
          {status === "revision_requested" && (
            <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]" data-testid={`${tid}-revision-state`}>
              Revision requested — your advisor is refining this recommendation. You may still approve it as presented.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy || isAdvisorPreview}
              onClick={approve}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[hsl(var(--accent-gold))]/20 hover:bg-[hsl(var(--accent-gold))]/30 disabled:opacity-50 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]"
              data-testid={`${tid}-approve-btn`}
              title={isAdvisorPreview ? "Client actions are disabled in advisor preview" : undefined}
            >
              {busy ? <Loader2 className="size-3 animate-spin" strokeWidth={1.5} /> : <Check className="size-3" strokeWidth={1.5} />}
              Approve these flights
            </button>
            <button
              type="button"
              disabled={busy || isAdvisorPreview}
              onClick={() => setPanelOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-[rgba(0,0,0,0.1)] hover:border-[hsl(var(--accent-gold))]/50 disabled:opacity-50 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-secondary))]"
              data-testid={`${tid}-revise-btn`}
              title={isAdvisorPreview ? "Client actions are disabled in advisor preview" : undefined}
            >
              <MessageCircle className="size-3" strokeWidth={1.5} />
              Request a revision
            </button>
          </div>
        </>
      )}

      {panelOpen && status !== "approved" && (
        <div className="rounded-md border border-[hsl(var(--accent-gold))]/25 bg-[hsl(var(--accent-gold))]/6 p-4 space-y-3" data-testid={`${tid}-revise-panel`}>
          <div className="flex items-start justify-between gap-3">
            <p className="font-serif text-[14.5px] text-[hsl(var(--foreground))]">
              What would you like changed about these flights?
            </p>
            <button type="button" onClick={() => setPanelOpen(false)} className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] p-0.5" aria-label="Close" data-testid={`${tid}-revise-close`}>
              <X className="size-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REVISION_PROMPTS.map((r) => {
              const on = reasons.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleReason(r)}
                  className={`px-3 py-1 rounded-full border font-serif text-[12.5px] transition-colors duration-150 ${
                    on
                      ? "border-[hsl(var(--accent-gold))]/60 bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--foreground))]"
                      : "border-[rgba(0,0,0,0.1)] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))]/40"
                  }`}
                  data-testid={`${tid}-revise-chip-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {r}
                </button>
              );
            })}
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="Tell us more — timing preferences, airlines you love or avoid, anything that helps us refine this…"
            className="w-full px-3 py-2 rounded border border-[rgba(0,0,0,0.08)] bg-white font-serif text-[13.5px] leading-relaxed"
            data-testid={`${tid}-revise-message`}
          />
          <button
            type="button"
            disabled={busy}
            onClick={sendRevision}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[hsl(var(--foreground))] text-white hover:bg-black disabled:opacity-50 font-mono text-[10px] uppercase tracking-[0.18em]"
            data-testid={`${tid}-revise-send`}
          >
            {busy ? <Loader2 className="size-3 animate-spin" strokeWidth={1.5} /> : <MessageCircle className="size-3" strokeWidth={1.5} />}
            Send to my advisor
          </button>
        </div>
      )}
    </div>
  );
}

function fmtMoney(cents, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(0)}`;
  }
}

function parseDur(str) {
  if (!str) return null;
  const h = /(\d+)\s*h/i.exec(str);
  const m = /(\d+)\s*m/i.exec(str);
  if (!h && !m) return null;
  return (h ? parseInt(h[1], 10) : 0) * 60 + (m ? parseInt(m[1], 10) : 0);
}

function fmtDur(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h ? `${h}h ` : ""}${m ? `${m}m` : ""}`.trim() || "0m";
}

const firstNames = (names) => names.map((n) => (n || "").split(" ")[0]).join(" & ");

/** Layover line — groups travelers by layover length and phrases the
 *  difference as "+ an additional Xh Ym (for Name)" when it differs. */
function LayoverLine({ segment, journeyTravelers, testId }) {
  if (!segment.layover_airport) return null;
  const assigned = (Array.isArray(segment.traveler_names) && segment.traveler_names.length > 0)
    ? segment.traveler_names
    : journeyTravelers.map((t) => t.name).filter(Boolean);
  const overrides = segment.traveler_layovers || {};
  const groups = new Map();
  for (const name of assigned) {
    const len = (overrides[name] || segment.layover_length || "").trim();
    if (!groups.has(len)) groups.set(len, []);
    groups.get(len).push(name);
  }
  const entries = [...groups.entries()].filter(([len]) => len);
  let detail = null;
  if (entries.length <= 1) {
    const len = entries.length ? entries[0][0] : (segment.layover_length || "").trim();
    detail = len ? <span className="text-[hsl(var(--foreground))]">— {len}</span> : null;
  } else {
    entries.sort((a, b) => {
      const da = parseDur(a[0]); const db = parseDur(b[0]);
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    });
    const baseDur = parseDur(entries[0][0]);
    detail = (
      <span className="text-[hsl(var(--foreground))]">
        — {entries[0][0]}
        <span className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]"> (for {firstNames(entries[0][1])})</span>
        {entries.slice(1).map(([len, names], k) => {
          const d = parseDur(len);
          const delta = baseDur !== null && d !== null && d > baseDur ? d - baseDur : null;
          return (
            <span key={k}>
              {delta !== null ? ` + an additional ${fmtDur(delta)}` : ` · ${len}`}
              <span className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]"> (for {firstNames(names)})</span>
            </span>
          );
        })}
      </span>
    );
  }
  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-1.5 font-serif text-[13.5px] text-[hsl(var(--foreground-secondary))]"
      data-testid={testId}
    >
      <Hourglass className="size-3.5 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} />
      <span>Layover in {segment.layover_airport} {detail}</span>
    </div>
  );
}

export default function FlightJourneyCard({ journey }) {
  const segments = journey.segments || [];
  const travelers = journey.travelers || [];
  const showPricing = Boolean(journey.show_pricing) && journey.total_cents !== null && journey.total_cents !== undefined;
  const hasOverrides = travelers.some((t) => t.departure_airport);

  return (
    <Surface variant="default" padded className="!p-6 space-y-5" data-testid={`flight-journey-card-${journey.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--accent-gold))]">
          <Plane className="size-3.5" strokeWidth={1.5} />
          {journey.label}
        </span>
        {journey.recommended && <AdvisorRecommendationBadge testId={`flight-journey-${journey.id}-badge`} />}
      </div>

      {journey.why_chosen && (
        <p
          className="font-accent italic text-[15px] sm:text-[16px] leading-relaxed text-[hsl(var(--foreground-secondary))] border-l-2 border-[hsl(var(--accent-gold))]/50 pl-4"
          data-testid={`flight-journey-${journey.id}-why`}
        >
          {journey.why_chosen}
        </p>
      )}

      {segments.length > 0 && (
        <div className="space-y-3" data-testid={`flight-journey-${journey.id}-segments`}>
          {segments.map((s, i) => (
            <div key={i} className="rounded-md border border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/50 px-4 py-3">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="font-serif text-[15px] text-[hsl(var(--foreground))]">
                  {[s.airline, s.flight_number].filter(Boolean).join(" · ") || "Flight"}
                </span>
                <span className="inline-flex items-center gap-1.5 font-mono text-[12px] tracking-[0.1em] text-[hsl(var(--foreground))]">
                  {s.departure_airport || "—"}
                  <ArrowRight className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  {s.arrival_airport || "—"}
                </span>
                {(s.departure_time || s.arrival_time) && (
                  <span className="font-serif text-[13px] text-[hsl(var(--foreground-secondary))]">
                    {[s.departure_time, s.arrival_time].filter(Boolean).join(" → ")}
                  </span>
                )}
                {s.departure_date && (
                  <span className="font-serif text-[13px] text-[hsl(var(--foreground-muted))]">
                    Departs {formatDate(s.departure_date)}
                    {/(\+\s?1|next day)/i.test(s.arrival_time || "") && " · arrives next day"}
                  </span>
                )}
                {s.duration && (
                  <span className="inline-flex items-center gap-1 font-serif text-[13px] text-[hsl(var(--foreground-muted))]">
                    <Clock className="size-3" strokeWidth={1.5} /> {s.duration}
                  </span>
                )}
              </div>
              {Array.isArray(s.traveler_names) && s.traveler_names.length > 0 && (
                <div
                  className="mt-1.5 flex items-center gap-1.5 font-serif text-[13.5px] text-[hsl(var(--foreground))]"
                  data-testid={`flight-journey-${journey.id}-segment-${i}-travelers`}
                >
                  <Users className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  {s.traveler_names.join(" · ")}
                  <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">only</span>
                </div>
              )}
              {s.layover_airport && (
                <LayoverLine
                  segment={s}
                  journeyTravelers={travelers}
                  testId={`flight-journey-${journey.id}-segment-${i}-layover`}
                />
              )}
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                {s.cabin && (
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]">{s.cabin}</span>
                )}
                {s.baggage_notes && (
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]">{s.baggage_notes}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {segments.length > 0 && segments.some((s) => Array.isArray(s.traveler_names) && s.traveler_names.length > 0) && travelers.length > 0 && (
        <div
          className="rounded-md border border-[hsl(var(--accent-gold))]/20 bg-[hsl(var(--accent-gold))]/5 px-4 py-3 space-y-1.5"
          data-testid={`flight-journey-${journey.id}-routes`}
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            Complete journey · by traveler
          </p>
          {travelers.map((t) => {
            const path = [];
            for (const s of segments) {
              const on = !(Array.isArray(s.traveler_names) && s.traveler_names.length > 0) || s.traveler_names.includes(t.name);
              if (!on) continue;
              if (!path.length) path.push(s.departure_airport || "?");
              path.push(s.arrival_airport || "?");
            }
            if (path.length < 2) return null;
            return (
              <div key={t.name} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5" data-testid={`flight-journey-${journey.id}-route-${(t.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="font-serif text-[13.5px] text-[hsl(var(--foreground))]">{t.name}</span>
                <span className="font-mono text-[11.5px] tracking-[0.12em] text-[hsl(var(--foreground-secondary))]">
                  {path.join(" → ")}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {(hasOverrides || showPricing) && travelers.length > 0 && (
        <div className="pt-1" data-testid={`flight-journey-${journey.id}-travelers`}>
          <div className="space-y-1.5">
            {travelers.map((t, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 border-b border-[rgba(0,0,0,0.04)] last:border-0 pb-1.5">
                <span className="font-serif text-[14px] text-[hsl(var(--foreground))]">
                  {t.name}
                  {t.type && t.type !== "adult" && (
                    <span className="ml-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">{t.type}</span>
                  )}
                  {t.departure_airport && (
                    <span className="ml-2 font-mono text-[10.5px] tracking-[0.08em] text-[hsl(var(--foreground-secondary))]">
                      departs {t.departure_airport}
                    </span>
                  )}
                </span>
                {showPricing && t.price_cents !== null && t.price_cents !== undefined && (
                  <span className="font-serif text-[14px] text-[hsl(var(--foreground-secondary))]">
                    {fmtMoney(t.price_cents, journey.currency)}
                  </span>
                )}
              </div>
            ))}
            {showPricing && (
              <div className="flex items-baseline justify-between gap-3 pt-1">
                <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">Journey total</span>
                <span className="font-serif text-[16px] text-[hsl(var(--foreground))]" data-testid={`flight-journey-${journey.id}-total`}>
                  {fmtMoney(journey.total_cents, journey.currency)}
                </span>
              </div>
            )}
            {showPricing && journey.pricing_note && (
              <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">{journey.pricing_note}</p>
            )}
          </div>
        </div>
      )}

      <ApprovalActions journey={journey} />
    </Surface>
  );
}
